import { useEffect, useRef, useState } from "react";

import { AnalyticsServiceV2, type PopularProduct } from "@/services/features/AnalyticsServiceV2";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import { getBrowserTimezone } from "@/utils/storeTime";

export interface PopularProductReportRow {
  rank: number;
  productId: string | null;
  title: string | null;
  category: string | null;
  triedOnCount: number;
  convertedCount: number;
  convertedRevenue: number;
  conversionRate: number;
}

export interface PopularProductsSalesQueryWindow {
  startDate: string;
  endDate: string;
  isLimited: boolean;
  isUnavailable: boolean;
  requiresCraveContact?: boolean;
}

export interface PopularProductsReportData {
  rows: PopularProductReportRow[];
  totalTryOns: number;
  totalConverted: number;
  totalRevenue: number;
  conversionRate: number;
  salesQueryWindow: PopularProductsSalesQueryWindow | null;
}

export type PopularProductsSalesAvailability = "checking" | "available" | "unavailable";

const POPULAR_PRODUCTS_LIMIT = 100;

const emptyReport: PopularProductsReportData = {
  rows: [],
  totalTryOns: 0,
  totalConverted: 0,
  totalRevenue: 0,
  conversionRate: 0,
  salesQueryWindow: null,
};

const normalizeCount = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const calculateConversionRate = (convertedCount: number, triedOnCount: number) =>
  triedOnCount > 0 ? (convertedCount / triedOnCount) * 100 : 0;

const buildReportData = (
  rows: PopularProductReportRow[],
  salesQueryWindow: PopularProductsSalesQueryWindow,
): PopularProductsReportData => {
  const totalTryOns = rows.reduce((sum, row) => sum + row.triedOnCount, 0);
  const totalConverted = rows.reduce((sum, row) => sum + row.convertedCount, 0);
  const totalRevenue = rows.reduce((sum, row) => sum + row.convertedRevenue, 0);

  return {
    rows,
    totalTryOns,
    totalConverted,
    totalRevenue,
    conversionRate: calculateConversionRate(totalConverted, totalTryOns),
    salesQueryWindow,
  };
};

const normalizePopularProducts = (
  data: PopularProduct[] | PopularProduct | null | undefined,
): PopularProduct[] => {
  if (!data) {
    return [];
  }

  return Array.isArray(data) ? data : [data];
};

const getValidLatestSalesDateKey = (
  latestSalesDate: Date | null | undefined,
  timezone: string,
): string | null => {
  if (!(latestSalesDate instanceof Date) || Number.isNaN(latestSalesDate.getTime())) {
    return null;
  }

  const dateKey = storePerformanceService.formatDate(latestSalesDate, timezone);
  if (!dateKey || dateKey <= "1970-01-01") {
    return null;
  }

  return dateKey;
};

const getSalesAvailability = async (
  analyticsService: AnalyticsServiceV2,
  stores: Store[],
  timezone: string,
  isActive: () => boolean,
): Promise<{
  oldestLatestSalesDateKey: string | null;
  hasNoSalesData: boolean;
}> => {
  const latestSalesDateKeys: string[] = [];

  for (const store of stores) {
    if (!isActive()) {
      return {
        oldestLatestSalesDateKey: null,
        hasNoSalesData: true,
      };
    }

    const latestSalesDate = await analyticsService.getLatestSalesDateForStore(store.id);
    if (!isActive()) {
      return {
        oldestLatestSalesDateKey: null,
        hasNoSalesData: true,
      };
    }

    const latestSalesDateKey = getValidLatestSalesDateKey(latestSalesDate, timezone);
    if (latestSalesDateKey) {
      latestSalesDateKeys.push(latestSalesDateKey);
    }
  }

  latestSalesDateKeys.sort();
  return {
    oldestLatestSalesDateKey: latestSalesDateKeys[0] ?? null,
    hasNoSalesData: latestSalesDateKeys.length === 0,
  };
};

export const usePopularProductsReport = (
  stores: Store[] | null,
  dateRange?: DateRange,
  deviceType?: string,
) => {
  const browserTimezone = getBrowserTimezone();
  const [data, setData] = useState<PopularProductsReportData>(emptyReport);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [salesAvailability, setSalesAvailability] =
    useState<PopularProductsSalesAvailability>("checking");
  const [error, setError] = useState<string | null>(null);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${deviceType}`
    : null;

  useEffect(() => {
    activeRequestIdRef.current += 1;

    if (!stores || stores.length === 0 || !dateRange) {
      setData(emptyReport);
      setLoading(false);
      setTableLoading(false);
      setSalesAvailability("checking");
      setError(null);
      setCompletedRequestKey(null);
      return;
    }

    const requestId = activeRequestIdRef.current;
    let cancelled = false;
    const isActive = () => !cancelled && activeRequestIdRef.current === requestId;

    const fetchData = async () => {
      setLoading(true);
      setTableLoading(true);
      setSalesAvailability("checking");
      setError(null);
      setData(emptyReport);

      try {
        const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
        const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);
        const storeIds = stores.map((store) => store.id);
        const analyticsService = AnalyticsServiceV2.getInstance();
        const effectiveDeviceType = deviceType && deviceType !== "all" ? deviceType : undefined;
        const { oldestLatestSalesDateKey, hasNoSalesData } = await getSalesAvailability(
          analyticsService,
          stores,
          browserTimezone,
          isActive,
        );

        if (!isActive()) {
          return;
        }

        const salesEndDate =
          oldestLatestSalesDateKey && oldestLatestSalesDateKey < endDate
            ? oldestLatestSalesDateKey
            : endDate;
        const salesWindowUnavailable =
          hasNoSalesData ||
          Boolean(oldestLatestSalesDateKey && oldestLatestSalesDateKey < startDate);
        const salesQueryWindow: PopularProductsSalesQueryWindow = {
          startDate,
          endDate: salesEndDate,
          isLimited: Boolean(oldestLatestSalesDateKey && oldestLatestSalesDateKey < endDate),
          isUnavailable: salesWindowUnavailable,
          requiresCraveContact: hasNoSalesData || undefined,
        };
        setSalesAvailability(salesWindowUnavailable ? "unavailable" : "available");
        setData(buildReportData([], salesQueryWindow));

        const popularProductsResponse = await analyticsService.getPopularProducts({
          storeIds,
          startDate,
          endDate: salesWindowUnavailable ? endDate : salesEndDate,
          deviceType: effectiveDeviceType,
          limit: POPULAR_PRODUCTS_LIMIT,
        });
        const popularProducts = normalizePopularProducts(popularProductsResponse.data).slice(
          0,
          POPULAR_PRODUCTS_LIMIT,
        );
        const rows: PopularProductReportRow[] = [];

        for (const [index, product] of popularProducts.entries()) {
          if (!isActive()) {
            return;
          }

          const triedOnCount = normalizeCount(product.count);
          let convertedCount = 0;
          let convertedRevenue = 0;

          if (product.productId && !salesWindowUnavailable) {
            const convertedSummaryResponse = await analyticsService.getConvertedProductSummary({
              storeIds,
              productId: product.productId,
              deviceType: effectiveDeviceType,
              startDate,
              endDate: salesEndDate,
            });
            convertedCount = normalizeCount(convertedSummaryResponse.data?.numberOfItemsSold);
            convertedRevenue = normalizeCount(convertedSummaryResponse.data?.totalPrice);
          }

          rows.push({
            rank: index + 1,
            productId: product.productId ?? null,
            title: product.title ?? null,
            category: product.category ?? null,
            triedOnCount,
            convertedCount,
            convertedRevenue,
            conversionRate: calculateConversionRate(convertedCount, triedOnCount),
          });
        }

        if (!isActive()) {
          return;
        }

        setData(buildReportData(rows, salesQueryWindow));
      } catch (err) {
        if (!isActive()) {
          return;
        }

        setError(err instanceof Error ? err.message : "Failed to load popular products report");
        setData(emptyReport);
      } finally {
        if (isActive()) {
          setLoading(false);
          setTableLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    stores?.map((store) => store.id).join(","),
    dateRange?.start?.getTime(),
    dateRange?.end?.getTime(),
    deviceType,
    browserTimezone,
    requestKey,
  ]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return {
    data,
    loading: loading || isRequestPending,
    tableLoading: tableLoading || isRequestPending,
    salesAvailability,
    error,
  };
};
